
import React, { useEffect, useState } from 'react'
import { Card, List, Button, Input, Space, Modal, message, Empty, Popconfirm } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { GeneralService } from '../../Services/GeneralService'
import { notify } from '../Customs/NotifyProvider'
import { formatDate } from '../Customs/dateFormatter'

import { canViewModule, canCreateModule, canUpdateModule, canDeleteModule } from '../../../utils/permissionUtils'

const MODULE_KEY = 'room_type'

const RoomType = () => {
    const [room_type, setAmenities] = useState([])
    const [newAmenity, setNewAmenity] = useState('')
    const [search, setSearch] = useState('')
    const [editing, setEditing] = useState(null)
    const [editingValue, setEditingValue] = useState('')
    const [isModalOpen, setIsModalOpen] = useState(false)

    const loadAmenities = async () => {
        try {
            const response = await GeneralService.get("room_types")
            if (response.status) {
                setAmenities(response.data)
            } else {
                console.error('Failed to load room_type: ' + response.message)
            }
        } catch (error) {
            console.error('Error loading room_type: ' + error.message)
        }
    }

    useEffect(() => {
        loadAmenities()
    }, [])

    if (!canViewModule(MODULE_KEY)) {
        return <Empty description="No permission to view this page" />
    }

    const persist = (next) => {
        setAmenities(next)
    }

    const handleAdd = () => {
        const value = newAmenity.trim()
        if (!value) {
            notify.warning('Enter an room type name to add')
            return
        }

        const payload = {
            type_name: value,
            updated_on: formatDate(),
        }

        console.log(payload);


        if (room_type.some(a => a.type_name === value)) {
            notify.warning('Room Type already added')
            return
        }

        const request = GeneralService.create("room_types", payload)
        request.then((response) => {
            if (response.status) {
                persist([...room_type, ...response.data])
                setNewAmenity('')
                notify.success('Room Type added')
            } else {
                notify.error('Failed to add room type: ', response.message)
            }
        }).catch((error) => {
            console.error('Error adding room type: ' + error.message)
            notify.error('Something went wrong while adding room type', error.message);
        })


    }

    const openEdit = (item) => {
        setEditing(item.id)
        setEditingValue(item.type_name)
        setIsModalOpen(true)
    }

    const handleEditSave = async () => {
        const value = editingValue.trim()
        if (!value) {
            notify.warning('Room Type cannot be empty')
            return
        }

        const payload = {
            type_name: value,
            updated_on: formatDate(),
        }

        try {
            const response = await GeneralService.edit(editing, 'room_types', payload)
            if (response.status) {
                const updated = room_type.map((a) => (a.id === editing ? { ...a, type_name: value, updated_on: formatDate() } : a))
                persist(updated)
                setIsModalOpen(false)
                setEditing(null)
                setEditingValue('')
                notify.success('Room Type updated')
            } else {
                notify.error('Failed to update room type: ' + response.message)
            }
        } catch (error) {
            console.error('Error updating room type: ' + error.message)
            notify.error('Something went wrong while updating room type')
        }
    }

    const handleDelete = async (item) => {
        try {
            console.log("item", item);

            const response = await GeneralService.delete('room_types', item.id)
            if (response.status) {
                const next = room_type.filter((a) => a.id !== item.id)
                persist(next)
                notify.success('Room Type deleted')
            } else {
                notify.error('Failed to delete room type: ' + response.message)
            }
        } catch (error) {
            console.error('Error deleting room type: ' + error.message)
            notify.error('Something went wrong while deleting room type')
        }
    }

    return (
        <Card title="Room Types" style={{ maxWidth: 800, margin: '0 auto' }}
            extra={
                <Input
                    size='large'
                    placeholder="Search room types"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ width: 320 }}
                    allowClear
                />
            }
        >


            <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                <Input
                    size='large'
                    allowClear
                    placeholder="Add room type (e.g., Standard delux suite)"
                    value={newAmenity}
                    onChange={(e) => setNewAmenity(e.target.value)}
                    onPressEnter={handleAdd}
                    style={{ width: 480 }}
                />
                {canCreateModule(MODULE_KEY) && (
                    <Button size='large' type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        Add
                    </Button>
                )}
            </Space>

            <List
                bordered
                dataSource={room_type.filter((item) =>
                    item.type_name.toLowerCase().includes(search.trim().toLowerCase())
                )}
                locale={{ emptyText: 'No room type added or found!' }}
                renderItem={(item) => (
                    <List.Item
                        actions={[
                            canUpdateModule(MODULE_KEY) && (
                                <Popconfirm
                                    key="edit"
                                    title="Edit this item?"
                                    description="Are you sure you want to edit this item?"
                                    okText="Yes, Edit"
                                    cancelText="Cancel"
                                    onConfirm={() => openEdit(item)}
                                >
                                    <Button type="text" icon={<EditOutlined />} />
                                </Popconfirm>
                            ),

                            canDeleteModule(MODULE_KEY) && (
                                <Popconfirm
                                    key="delete"
                                    title="Delete this item?"
                                    description="Are you sure you want to delete this item?"
                                    okText="Yes, Delete"
                                    cancelText="Cancel"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => handleDelete(item)}
                                >
                                    <Button type="text" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                            ),
                        ].filter(Boolean)}
                    >
                        {item.type_name}
                    </List.Item>
                )}
            />

            <Modal
                title="Edit Room Type"
                open={isModalOpen}
                onOk={handleEditSave}
                onCancel={() => setIsModalOpen(false)}
                okText="Save"
            >
                <Input
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onPressEnter={handleEditSave}
                />
            </Modal>
        </Card>
    )
}

export default RoomType
